import React, { useState, useEffect } from 'react';
import { schedulesAPI, jobsAPI, contractorsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar, Plus, Trash2, Clock, MapPin, User } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, startOfWeek, addDays, isWithinInterval, isSameDay } from 'date-fns';

export const SchedulePage = () => {
  const { user } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    job_id: '',
    contractor_id: '',
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schedulesRes, jobsRes, contractorsRes] = await Promise.all([
        schedulesAPI.getAll(),
        jobsAPI.getAll(),
        contractorsAPI.getAll()
      ]);
      setSchedules(schedulesRes.data);
      setJobs(jobsRes.data);
      setContractors(contractorsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await schedulesAPI.create(formData);
      toast.success('Schedule created');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    try {
      await schedulesAPI.delete(id);
      toast.success('Schedule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete schedule');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setFormData({
      job_id: '',
      contractor_id: '',
      title: '',
      description: '',
      start_datetime: '',
      end_datetime: '',
    });
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const getSchedulesForDay = (date) => {
    return schedules.filter(schedule => {
      const scheduleDate = parseISO(schedule.start_datetime);
      return isSameDay(scheduleDate, date);
    });
  };

  const getJobTitle = (jobId) => jobs.find(j => j.id === jobId)?.title || 'Unknown Job';
  const getContractorName = (contractorId) => contractors.find(c => c.id === contractorId)?.name || 'Unknown';
  const getJobAddress = (jobId) => jobs.find(j => j.id === jobId)?.address || '';

  const navigateWeek = (direction) => {
    setCurrentWeekStart(prev => addDays(prev, direction * 7));
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Schedule</h1>
          <p className="text-muted-foreground mt-1">Book and manage trade schedules</p>
        </div>
        {user?.role !== 'trade' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="font-semibold rounded-lg" data-testid="add-schedule-btn">
                <Plus className="h-4 w-4 mr-2" />
                Book Trade
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader>
                <DialogTitle>Book Trade</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    placeholder="e.g., Kitchen Plumbing"
                    className="bg-input border-border rounded-lg"
                    data-testid="schedule-title"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Job *</Label>
                    <Select value={formData.job_id} onValueChange={(v) => setFormData({ ...formData, job_id: v })}>
                      <SelectTrigger className="bg-input border-border rounded-lg" data-testid="schedule-job">
                        <SelectValue placeholder="Select job" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {jobs.map(job => (
                          <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Contractor *</Label>
                    <Select value={formData.contractor_id} onValueChange={(v) => setFormData({ ...formData, contractor_id: v })}>
                      <SelectTrigger className="bg-input border-border rounded-lg" data-testid="schedule-contractor">
                        <SelectValue placeholder="Select contractor" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {contractors.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name} - {c.trade_type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date/Time *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.start_datetime}
                      onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                      required
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date/Time *</Label>
                    <Input
                      type="datetime-local"
                      value={formData.end_datetime}
                      onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                      required
                      className="bg-input border-border rounded-lg"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-input border-border rounded-lg"
                    rows={3}
                    placeholder="Details about the work to be done..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                  <Button type="submit" className="font-semibold rounded-lg" data-testid="schedule-submit">
                    Book
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => navigateWeek(-1)} className="rounded-lg">
          Previous Week
        </Button>
        <h2 className="text-lg font-bold">
          {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
        </h2>
        <Button variant="outline" onClick={() => navigateWeek(1)} className="rounded-lg">
          Next Week
        </Button>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4">
          {weekDays.map((day) => {
            const daySchedules = getSchedulesForDay(day);
            const isToday = isSameDay(day, new Date());
            
            return (
              <Card key={day.toISOString()} className={`bg-card border-border rounded-lg ${isToday ? 'ring-2 ring-primary' : ''}`}>
                <CardHeader className="p-3 border-b border-border">
                  <div className="text-center">
                    <p className="text-xs text-muted-foreground uppercase">{format(day, 'EEE')}</p>
                    <p className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>{format(day, 'd')}</p>
                  </div>
                </CardHeader>
                <CardContent className="p-2 min-h-[200px]">
                  {daySchedules.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center mt-4">No bookings</p>
                  ) : (
                    <div className="space-y-2">
                      {daySchedules.map((schedule) => (
                        <div
                          key={schedule.id}
                          className="p-2 bg-primary/10 border border-primary/30 rounded-lg text-xs"
                          data-testid={`schedule-item-${schedule.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <p className="font-medium text-primary line-clamp-1">{schedule.title}</p>
                            {user?.role !== 'trade' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4"
                                onClick={() => handleDelete(schedule.id)}
                              >
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            )}
                          </div>
                          <div className="mt-1 space-y-1 text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>{format(parseISO(schedule.start_datetime), 'HH:mm')}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span className="truncate">{getContractorName(schedule.contractor_id)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate">{getJobAddress(schedule.job_id)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SchedulePage;
